/**
 * MessagesPage — Full page messaging view with master-detail layout.
 *
 * Left panel: Thread list grouped by Account (customer), then by entity type.
 * Right panel: MessageThread conversation for the selected entity.
 *
 * Routes: /messages (inbox) and /messages/:entityWsid (thread detail)
 */

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Input,
  Typography,
  Badge,
  Spin,
  Empty,
  Divider,
  Tag,
  theme,
} from "antd";
import {
  SearchOutlined,
  MessageOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { useMessageInbox } from "@/hooks/useMessageInbox";
import { ThreadListItem } from "@/components/messaging/ThreadListItem";
import { MessageThread } from "@/components/messaging/MessageThread";

const { Text, Title } = Typography;

export const MessagesPage: React.FC = () => {
  const {
    token: { colorBgContainer, colorBorder, borderRadiusLG, fontSizeSM },
  } = theme.useToken();

  const { entityWsid } = useParams<{ entityWsid?: string }>();
  const navigate = useNavigate();
  const { accountGroups, groups, totalUnread, isLoading } = useMessageInbox();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(
    new Set()
  );

  const toggleAccount = (accountId: string) => {
    setCollapsedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  // Filter by module type and search
  const filteredAccountGroups = accountGroups
    .map((ag) => {
      let moduleGroups = ag.moduleGroups;

      // Filter by module type
      if (activeFilter) {
        moduleGroups = moduleGroups.filter(
          (mg) => mg.moduleName === activeFilter
        );
      }

      // Filter by search query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        moduleGroups = moduleGroups
          .map((mg) => ({
            ...mg,
            threads: mg.threads.filter(
              (t) =>
                t.entityName.toLowerCase().includes(q) ||
                t.lastMessage.toLowerCase().includes(q) ||
                t.accountName.toLowerCase().includes(q)
            ),
          }))
          .filter((mg) => mg.threads.length > 0);
      }

      if (moduleGroups.length === 0) return null;
      return {
        ...ag,
        moduleGroups,
        totalUnread: moduleGroups.reduce((s, mg) => s + mg.totalUnread, 0),
      };
    })
    .filter(Boolean) as typeof accountGroups;

  const moduleFilters = groups.map((g) => ({
    name: g.moduleName,
    label: g.label,
    unread: g.totalUnread,
  }));

  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        height: "calc(100vh - 96px)",
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        overflow: "hidden",
        border: `1px solid ${colorBorder}`,
      }}
    >
      {/* Left: Thread list */}
      <div
        style={{
          width: 360,
          flexShrink: 0,
          borderRight: `1px solid ${colorBorder}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ padding: "16px 16px 12px" }}>
          <Title level={4} style={{ margin: 0, marginBottom: 12 }}>
            <MessageOutlined style={{ marginRight: 8 }} />
            Messaggi
            {totalUnread > 0 && (
              <Badge
                count={totalUnread}
                style={{ backgroundColor: "#6366f1", marginLeft: 8 }}
              />
            )}
          </Title>

          {/* Search */}
          <Input
            placeholder="Cerca conversazioni..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            size="small"
            style={{ marginBottom: 8 }}
          />

          {/* Filter pills */}
          {moduleFilters.length > 1 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Tag
                color={activeFilter === null ? "blue" : undefined}
                style={{ cursor: "pointer", margin: 0 }}
                onClick={() => setActiveFilter(null)}
              >
                Tutti
              </Tag>
              {moduleFilters.map((mf) => (
                <Tag
                  key={mf.name}
                  color={activeFilter === mf.name ? "blue" : undefined}
                  style={{ cursor: "pointer", margin: 0 }}
                  onClick={() =>
                    setActiveFilter(
                      activeFilter === mf.name ? null : mf.name
                    )
                  }
                >
                  {mf.label}
                  {mf.unread > 0 && (
                    <Badge
                      count={mf.unread}
                      size="small"
                      style={{
                        backgroundColor: "#6366f1",
                        marginLeft: 4,
                        boxShadow: "none",
                      }}
                    />
                  )}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Thread list grouped by Account */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : filteredAccountGroups.length === 0 ? (
            <Empty
              description={
                searchQuery ? "Nessun risultato" : "Nessuna conversazione"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: "40px 0" }}
            />
          ) : (
            filteredAccountGroups.map((acctGroup) => {
              const key = acctGroup.accountId || "_none";
              const collapsed = collapsedAccounts.has(key);
              return (
                <div key={key}>
                  {/* Account header */}
                  <div
                    onClick={() => toggleAccount(key)}
                    style={{
                      padding: "8px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      background: "rgba(99, 102, 241, 0.04)",
                      borderBottom: `1px solid ${colorBorder}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <BankOutlined style={{ color: "#6366f1" }} />
                      {collapsed ? "▸" : "▾"} {acctGroup.accountName}
                    </Text>
                    {acctGroup.totalUnread > 0 && (
                      <Badge
                        count={acctGroup.totalUnread}
                        size="small"
                        style={{ backgroundColor: "#6366f1" }}
                      />
                    )}
                  </div>

                  {/* Module subgroups within account */}
                  {!collapsed &&
                    acctGroup.moduleGroups.map((modGroup) => (
                      <div key={modGroup.moduleName}>
                        {acctGroup.moduleGroups.length > 1 && (
                          <div
                            style={{
                              padding: "4px 16px 4px 32px",
                              background: "rgba(0,0,0,0.015)",
                              borderBottom: `1px solid ${colorBorder}`,
                              position: "sticky",
                              top: 37,
                              zIndex: 1,
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{
                                fontSize: fontSizeSM - 1,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {modGroup.label}
                            </Text>
                          </div>
                        )}
                        {modGroup.threads.map((thread) => (
                          <ThreadListItem
                            key={thread.entityId}
                            thread={thread}
                            selected={thread.entityId === entityWsid}
                            onClick={() =>
                              navigate(`/messages/${thread.entityId}`)
                            }
                          />
                        ))}
                      </div>
                    ))}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Thread detail */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {entityWsid ? (
          <MessageThread entityId={entityWsid} />
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Empty
              description="Seleziona una conversazione"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
};
